import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { Subscription } from "rxjs";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { DxmDownloadStatus } from "../../model/dxm-download-status";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-status-bar",
  templateUrl: "./status-bar.component.html",
  styleUrls: ["./status-bar.component.scss"],
  standalone: false,
})
export class StatusBarComponent {
  subscriptions: Subscription[] = [];

  private _mapCenterCoordinates: number[] = [];

  _visibleDate: string;

  _target: string;

  _centerAOI;

  _measurementToolStatus: boolean;

  _dsmStatusEvent;

  _discreteEnabled: boolean = false;

  statusOrtho: string;

  statusOrthoFinal: boolean;

  statusOrthoTooltip: string;

  measurementStatus: string;

  showMeasurementStatus: boolean;

  measurementStatusAvailable: boolean;

  measurementStatusPreview: boolean;

  measurementStatusTooltip: string;

  @Input() zoom: number;

  @Input()
  set centerAOI(aoi: IProjectAOI) {
    this._centerAOI = aoi;
    if (this.discreteEnabled) {
      this.getMeasurementStatus(aoi);
    } else {
      this.getStatusOrtho(aoi);
    }
  }

  @Input()
  set discreteEnabled(enabled: boolean) {
    this._discreteEnabled = enabled;
    this.getMeasurementStatus(this._centerAOI);
  }

  get discreteEnabled(): boolean {
    return this._discreteEnabled;
  }

  @Input()
  set dsmStatus(event: any) {
    this._dsmStatusEvent = event;
    this.getMeasurementStatus(this._centerAOI);
  }

  @Input()
  set measurementToolStatus(status) {
    this._measurementToolStatus = status;
    this.getMeasurementStatus(this._centerAOI);
  }

  @Input()
  set target(target: string) {
    this._target = target;
  }

  get target(): string {
    return this._target;
  }

  @Input()
  set visibleDate(date: string) {
    this._visibleDate = date;
  }

  get visibleDate(): string {
    return this._visibleDate;
  }

  @Input()
  set mapCenterCoordinates(coordinates: number[]) {
    this._mapCenterCoordinates = coordinates;
  }

  get mapCenterCoordinates(): number[] {
    return this._mapCenterCoordinates;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  showLocationInfo(): boolean {
    return !!this.mapCenterCoordinates && !!this.mapCenterCoordinates.length;
  }

  getStatusOrtho(collection: IProjectAOI) {
    if (!collection) {
      this.statusOrtho = "";
    } else {
      if (
        ([ViewerConstants.COVERAGE_TYPE_STATUS.FINAL, ViewerConstants.COVERAGE_TYPE_STATUS.DXM].includes(
          collection.getCoverageType(),
        ) &&
          (collection.isUrban() || collection.isWide())) ||
        collection.isLegacy()
      ) {
        this.statusOrtho = ViewerConstants.STATUS_ORTHO.FINAL;
        this.statusOrthoFinal = true;
        this.statusOrthoTooltip = "This is the final ortho state of this imagery product.";
      } else if (
        (collection.isUrban() || collection.isWide()) &&
        [ViewerConstants.COVERAGE_TYPE_STATUS.QUICK, ViewerConstants.COVERAGE_TYPE_STATUS.FIRST].includes(
          collection.getCoverageType(),
        )
      ) {
        this.statusOrtho = ViewerConstants.STATUS_ORTHO.PREVIEW;
        this.statusOrthoFinal = false;
        this.statusOrthoTooltip =
          "Preview Ortho allows early access to vertical imagery. There may be temporary artifacts until the Final Ortho is published.";
      } else if (collection.isGraySky()) {
        this.statusOrtho = ViewerConstants.STATUS_ORTHO.GRAYSKY;
        this.statusOrthoFinal = true;
        this.statusOrthoTooltip = "Gray Sky Ortho provides rapid access to post-catastrophe imagery.";
      }
    }
  }

  getMeasurementStatus(collection: IProjectAOI) {
    if (collection && this._measurementToolStatus && this._discreteEnabled && this._dsmStatusEvent) {
      this.showMeasurementStatus = true;
      if (this._dsmStatusEvent.enabled) {
        this.measurementStatus = "Measuring Data";
        this.measurementStatusAvailable = true;
        this.measurementStatusPreview = false;
        this.measurementStatusTooltip =
          "3D measurements are generated from Vexcel's unique DSM data. This collection contains DSM data so 3D measurements are available. To learn more, visit: https://vexceldata.com/products/digital-surface-models/";
      } else if (collection.isLegacy()) {
        this.measurementStatus = "Legacy Data";
        this.measurementStatusAvailable = false;
        this.measurementStatusPreview = false;
        this.measurementStatusTooltip =
          "3D measurements are generated from Vexcel's unique DSM data. Legacy data existed before DSM generation, so 3D measurement capabilities are not available for this collection.";
      } else if (
        (collection.isUrban() ||
          [ViewerConstants.COVERAGE_TYPE_STATUS.QUICK, ViewerConstants.COVERAGE_TYPE_STATUS.FIRST].includes(
            collection.getCoverageType(),
          )) &&
        this._dsmStatusEvent.status === "finished"
      ) {
        this.measurementStatus = "Preview Data";
        this.measurementStatusAvailable = false;
        this.measurementStatusPreview = true;
        this.measurementStatusTooltip =
          "3D measurements are generated from Vexcel's unique DSM data, which is currently being processed. Upgraded data and 3D measurement capabilities for this collection are coming soon. To learn more, visit: https://vexceldata.com/products/digital-surface-models/";
      } else if (
        !this._dsmStatusEvent.inside &&
        !this._dsmStatusEvent.coverage.includes(Infinity) &&
        this._dsmStatusEvent.status !== "finished"
      ) {
        this.measurementStatus = "No DSM Data";
        this.measurementStatusAvailable = false;
        this.measurementStatusPreview = false;
        this.measurementStatusTooltip = "There is no DSM data in this area. 3D measuring tools are not compatible.";
      } else {
        this.showMeasurementStatus = false;
        this.measurementStatus = "";
        this.measurementStatusTooltip = "";
      }
    } else {
      this.showMeasurementStatus = false;
      this.measurementStatus = "";
      this.measurementStatusTooltip = "";
    }
  }

  availableMeasurementStatus(): boolean {
    return (
      this.showMeasurementStatus &&
      (this._dsmStatusEvent.status === DxmDownloadStatus.finished ||
        this._dsmStatusEvent.status === DxmDownloadStatus.stop)
    );
  }
}
